import React, { useEffect, useState } from 'react';
import { CheckCircle, Info } from '@phosphor-icons/react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { toast } from 'react-toastify';

import {
  trackCreateProject,
  trackSampleUpsellClickNextEventForm,
  trackSampleUpsellSubmitNextEventForm,
  trackSampleUpsellClickAskCC,
} from '../../../../analytics';

import { Form, FloatingLabel } from '../../../../components/form';
import {
  Button,
  ButtonLink,
  Modal,
  Tooltip,
} from '../../../../components/general';

import { URL, FORMSPARK_ID } from '../../../../constants';
import { isEmpty } from '../../../../utils';

import { validateForm } from './nextEventFormValidations';

interface Props {
  show: boolean;
}

const SampleUpsellModal: React.FC<Props> = (props) => {
  const { show } = props;

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    date: '',
    numOfPeople: '',
    msg: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (show)
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        zIndex: 1060,
      });
  }, [show]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = await validateForm(formData);

    if (!isEmpty(errors)) {
      setErrors(errors);
      toast.error('Some data is missing or invalid');

      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(
        `https://submit-form.com/${FORMSPARK_ID.NEXT_EVENT}`,
        formData,
      );

      toast.success("Thanks, we'll get back to you soon.");
      setShowForm(false);
      trackSampleUpsellSubmitNextEventForm();
    } catch (error) {
      toast.error('There was a problem submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      {/* main modal */}
      <Modal show={show && !showForm}>
        <Modal.Body centered>
          <CheckCircle className="w-24 h-24 text-[var(--success-color)]" />

          <h4>You did it!</h4>

          <div className="text-lg">
            You’ve successfully generated a personalised video which is now on
            its way to your inbox.{' '}
            <Tooltip
              text={
                <div>
                  This is an <b>example video</b> and it doesn't reflect the
                  choices you've made before.
                </div>
              }
              className="inline-block"
            >
              <span>
                <Info className="cursor-pointer hover:fill-[url('#icon-primary-gradient')]" />
              </span>
            </Tooltip>
          </div>

          <div className="text-lg">Where to from here?</div>

          <div className="w-full sm:w-4/5 flex flex-col items-center justify-center gap-4">
            <ButtonLink
              to={URL.PROJECT_SETTINGS}
              onClick={() => trackCreateProject('Sample upsell modal')}
              className="w-full"
            >
              Create Project
            </ButtonLink>

            <Button
              onClick={() => {
                setShowForm(true);
                trackSampleUpsellClickNextEventForm();
              }}
              className="w-full"
            >
              Tell Us About Your Next Event
            </Button>

            <Button
              onClick={trackSampleUpsellClickAskCC}
              colour="salmon-outline"
              className="w-full !text-black disabled:opacity-100"
              withGap
              disabled
            >
              Ask CrowdClip&reg;!
              <span className="text-xs">Coming Soon</span>
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* next event modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Body centered>
          <h4>
            Tell us a little bit more about your upcoming event. One of our
            superstar team members will get in touch with you.
          </h4>

          <Form onSubmit={handleSubmit} formClassName="w-full text-left">
            <div className="flex flex-col sm:flex-row gap-4">
              <FloatingLabel
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                isInvalid={!!errors.name}
                errMsg={errors.name}
                required
                className="w-full sm:w-1/2"
              />

              <FloatingLabel
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
                errMsg={errors.email}
                required
                className="w-full sm:w-1/2"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <FloatingLabel
                label="Event Date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                isInvalid={!!errors.date}
                errMsg={errors.date}
                required
                className="w-full sm:w-1/2"
              />

              <FloatingLabel
                label="Number of people"
                name="numOfPeople"
                value={formData.numOfPeople}
                onChange={handleChange}
                isInvalid={!!errors.numOfPeople}
                errMsg={errors.numOfPeople}
                required
                className="w-full sm:w-1/2"
              />
            </div>

            <FloatingLabel
              label="Message"
              as="textarea"
              name="msg"
              value={formData.msg}
              onChange={handleChange}
              isInvalid={!!errors.msg}
              errMsg={errors.msg}
              required
            />

            <Button className="w-full" isSubmitBtn isProcessing={isSubmitting}>
              Submit
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default SampleUpsellModal;
